<template>
  <div class="conversations">
    <div class="cell">
      <div class="conversations__search">
        <div class="conversations__search-icon">
          <search-icon />
        </div>
        <input
          v-model.trim="searchQuery"
          class="conversations__search-input"
          type="text"
          :placeholder="$t('chat.search')"
        />
        <div class="conversations__search-badge">
          {{ conversationsCount }}
        </div>
      </div>
    </div>
    <div class="cell auto">
      <div class="conversations__block">
        <vue-scroll>
          <div class="conversations__list">
            <loader-conversation v-if="isConversationsLoading" />
            <template v-else>
              <conversation-empty
                v-if="isConversationsEmpty"
                :text="'No conversations'"
              />
              <template v-else>
                <conversation-empty
                  v-if="isResultsEmpty"
                  :text="'No results'"
                />
                <conversation-history-item
                  v-else
                  v-for="(conversation, i) in orderedConversations"
                  :key="`${i}-${conversation.client.id}`"
                  :conversation="conversation"
                  @clicked="markConversationAsSelected(conversation)"
                />
              </template>
            </template>
          </div>
        </vue-scroll>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import orderBy from 'lodash/orderBy';
import {
  CONVERSATION_LOAD_HISTORY,
  CONVERSATION_LOAD_MESSAGES,
  MESSAGES_LOADED,
} from '@/eventTypes';
import SearchIcon from '@/assets/icons/search-w.svg';
import listConversationsHistoryGql from '@/graphql/queries/listConversationsHistory.gql';
import getMessagesByClientIdGql from '@/graphql/queries/getMessagesByClientId.gql';
import lastConversationByClientIdGql from '@/graphql/queries/lastConversationByClientId.gql';
import EventBus from '@/eventBus';
import errorHandler from '@/utils/error.handler';

export default {
  components: {
    SearchIcon,
    ConversationEmpty: () => import('@/components/ConversationEmpty.vue'),
    ConversationHistoryItem: () => import('@/components/history/ConversationHistoryItem.vue'),
    LoaderConversation: () => import('@/components/loaders/Conversation.vue'),
  },
  props: {
    theme: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    searchQuery: '',
    page: 1,
    length: 15,
    listConversationsHistory: [],
    isConversationsLoading: false,
    delayedSearch: null,
  }),
  computed: {
    ...mapState({
      conversationsHistory: (state) => state.conversationsHistory,
      activeClientHistoryId: (state) => state.activeClientHistoryId,
      isVisible: (state) => state.appIsVisible,
    }),
    ...mapGetters(['activeConversationHistory']),
    campaignId() {
      return this.activeConversationHistory
      && this.activeConversationHistory.conversation
      && this.activeConversationHistory.conversation.campaign
        ? this.activeConversationHistory.conversation.campaign.id : null;
    },
    conversationsCount() {
      return this.conversationsHistory.length;
    },
    isConversationsEmpty() {
      return this.conversationsCount === 0;
    },
    isResultsEmpty() {
      return this.conversationsHistory.length === 0;
    },
    orderedConversations() {
      return orderBy(this.conversationsHistory, [(conversation) => {
        const lastMessage = conversation.messages.length > 0
          ? conversation.messages[conversation.messages.length - 1] : null;

        if (lastMessage) {
          return this.$moment(lastMessage.createdAt);
        }

        return null;
      }], ['desc']);
    },
    darkTheme() {
      return this.theme === 'dark';
    },
  },
  watch: {
    searchQuery(val) {
      clearTimeout(this.delayedSearch);

      this.delayedSearch = setTimeout(() => {
        this.getHistory(val);
      }, 1000);
    },
    '$route.path': function routeWatcher() {
      this.searchQuery = '';
    },
  },
  created() {
    this.subscribeConversations();
  },
  activated() {
    this.subscribeConversations();
  },
  mounted() {
    this.getHistory();
  },
  deactivated() {
    this.unsubscribeConversations();
  },
  destroyed() {
    this.unsubscribeConversations();
  },
  methods: {
    ...mapMutations([
      'addPreviousMessagesHistory',
      'setActiveClientHistory',
      'addConversationsHistory',
      'addMessageToConversation',
      'setClientHistoryClosed',
    ]),
    ...mapMutations({
      setLoader: 'updateConversationLoadingState',
      setFirstLoad: 'updateConversationFirstLoadState',
      setTopReach: 'updateConversationTopReachedState',
    }),
    async getHistory(search = '') {
      this.isConversationsLoading = true;
      const { data } = await this.$apollo.query({
        query: listConversationsHistoryGql,
        variables: {
          page: this.page,
          length: this.length,
          search,
        },
        fetchPolicy: 'no-cache',
      });

      const result = data.historyConversations.data.map(({ messages, conversation, ...params }) => {
        const existsConversation = this.conversationsHistory.find(
          ({ conversation: hConv }) => hConv.id === conversation.id,
        );

        if (existsConversation) {
          return {
            ...params,
            conversation,
            messages: existsConversation.messages,
          };
        }

        return { ...params, conversation, messages };
      });

      this.addConversationsHistory(result);
      this.isConversationsLoading = false;
    },
    markConversationAsSelected(conversation) {
      this.setActiveClientHistory(conversation);
    },
    async getMessages(clientId, lastId) {
      if (clientId.length !== 0) {
        this.setLoader({
          clientId,
          loading: true,
        });
        try {
          const { data: response } = await this.$apollo.query({
            query: getMessagesByClientIdGql,
            variables: {
              clientId: this.activeConversationHistory.client.id,
              channelId: this.activeConversationHistory.conversation.channel.id,
              lastId,
              length: 20,
            },
          });

          const messages = response.getMessages.data;

          this.addPreviousMessagesHistory({
            clientId,
            messages,
          });

          if (messages.length > 0) {
            this.setFirstLoad({
              clientId,
              firstLoad: false,
            });
          } else {
            this.setTopReach({
              clientId,
              topReached: true,
            });
          }
        } catch (e) {
          errorHandler.logErrors(e);
        } finally {
          this.setLoader({
            clientId,
            loading: false,
          });
        }

        EventBus.$emit(MESSAGES_LOADED, clientId);
      }
    },
    async loadClientHistory(clientId) {
      try {
        const { data: result } = await this.$apollo.query({
          query: lastConversationByClientIdGql,
          fetchPolicy: 'no-cache',
          variables: {
            clientId,
            campaignId: this.campaignId,
          },
        });

        const history = result.lastConversationByClientId;

        this.setClientHistoryClosed({
          clientId,
          history,
        });
      } catch (e) {
        errorHandler.logErrors(e);
      }
    },
    normalizeString(str) {
      return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    },
    subscribeConversations() {
      EventBus.$on(CONVERSATION_LOAD_MESSAGES, (clientId, messageId) => {
        if (messageId) {
          this.getMessages(clientId, messageId);
        }
      });

      EventBus.$on(CONVERSATION_LOAD_HISTORY, (clientId) => {
        if (this.campaignId) {
          this.loadClientHistory(clientId);
        }
      });
    },
    unsubscribeConversations() {
      EventBus.$off(CONVERSATION_LOAD_MESSAGES);
      EventBus.$off(CONVERSATION_LOAD_HISTORY);
    },
  },
};
</script>
